/**
 * Schema-driven entity types for the CSV import UI.
 *
 * Replaces the previously hardcoded contact/company/deal/note(/user) lists so
 * that custom (`org_*`) entity types can be imported into and linked to. The
 * sets mirror the backend policy (apps/csv_imports/entity_type_policy.py):
 *
 *   importable     = core system record types + every custom domain type
 *   assoc. targets = importable + `ahoy_user` (link-only)
 *
 * `useObjectTypes` already excludes pipeline/stage/system/`*_timeline` types,
 * which is exactly the custom-domain set we want.
 */

import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { hasEntityPermission } from '@/hooks/usePermissions';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';

export interface ImportTypeOption {
  value: string;
  label: string;
}

/** Event type granting permission to create records of an entity type. */
const OBJECT_CREATED_EVENT = 'ahoy_object_created';

/** Core system record types that can always be imported into, in display order. */
const CORE_IMPORTABLE_TYPES: ImportTypeOption[] = [
  { value: 'ahoy_contact', label: 'Contact' },
  { value: 'ahoy_company', label: 'Company' },
  { value: 'ahoy_deal', label: 'Deal' },
  { value: 'ahoy_note', label: 'Note' },
];

/** Link-only target: you cannot import users, but you can assign them as owners
 * (e.g. match an existing org member by email). */
const USER_TARGET: ImportTypeOption = {
  value: 'ahoy_user',
  label: 'User (org member)',
};

/**
 * Link-only targets, same shape as USER_TARGET: a CSV can never create a stage,
 * but "match this cell against an existing stage by name" is just the generic
 * CRM-entity association lookup.
 *
 * `useObjectTypes` strips stage types because they aren't importable — correct
 * for that question, but it leaked into this one, which is why stage mapping
 * ended up with its own parallel rail instead of using the association mapper.
 *
 * Custom case-kind types have their own stage types and are NOT covered here
 * — see the note in useImportTargetTypes.
 */
const BASE_STAGE_TARGETS: ImportTypeOption[] = [
  { value: 'ahoy_contact_lifecycle_stage', label: 'Contact Lifecycle Stage' },
  { value: 'ahoy_deal_pipeline_stage', label: 'Deal Pipeline Stage' },
];

const TASK_STAGE_TARGET: ImportTypeOption = {
  value: 'ahoy_task_pipeline_stage',
  label: 'Task Pipeline Stage',
};

const TASK_IMPORT_TARGET: ImportTypeOption = {
  value: 'ahoy_task',
  label: 'Task',
};

const CORE_IMPORTABLE_NAMES = new Set(CORE_IMPORTABLE_TYPES.map(t => t.value));

function useCustomDomainTypes(orgId: number | undefined): ImportTypeOption[] {
  const { data } = useObjectTypes(orgId);
  return useMemo(
    () =>
      (data?.items ?? [])
        .filter(t => !CORE_IMPORTABLE_NAMES.has(t.entity_type_name))
        .map(t => ({ value: t.entity_type_name, label: t.label })),
    [data?.items]
  );
}

/**
 * Entity types the importing user can import into: core system record types
 * plus the org's custom domain types, filtered to those the current user has
 * permission to CREATE (`ahoy_object_created`). A user is only offered types
 * they can actually write — so the import can never propose a record the
 * server would reject for lack of permission.
 */
export function useImportableEntityTypes(
  orgId: number | undefined
): ImportTypeOption[] {
  const custom = useCustomDomainTypes(orgId);
  const { currentOrg } = useOrg();
  const permissions = currentOrg?.permissions as
    | Array<{ entity_type_name?: string; event_type_name?: string }>
    | undefined;
  return useMemo(
    () =>
      [...CORE_IMPORTABLE_TYPES, TASK_IMPORT_TARGET, ...custom].filter(t =>
        hasEntityPermission(permissions, t.value, OBJECT_CREATED_EVENT)
      ),
    [custom, permissions]
  );
}

/**
 * Entity types that can be the target of a CRM-entity association lookup:
 * `ahoy_user` and the system stage types (both link-only) plus everything
 * importable.
 *
 * Mirrors `get_association_target_types` in
 * `apps/csv_imports/entity_type_policy.py` — the server validates every mapping
 * against its own set, so anything offered here must be in
 * `EXTRA_ASSOCIATION_TARGET_TYPES` there or the import is rejected.
 *
 * KNOWN GAP: custom case-kind entity types have their own pipeline stage types,
 * which are not listed. Those need deriving from the schema rather than a static
 * list — `useObjectTypes` filters stage types out entirely, so there is no
 * client-side source for them today.
 */
export function useImportTargetTypes(
  orgId: number | undefined
): ImportTypeOption[] {
  const custom = useCustomDomainTypes(orgId);
  return useMemo(() => {
    const stageTargets = [...BASE_STAGE_TARGETS, TASK_STAGE_TARGET];
    return [
      USER_TARGET,
      ...stageTargets,
      ...CORE_IMPORTABLE_TYPES,
      TASK_IMPORT_TARGET,
      ...custom,
    ];
  }, [custom]);
}
